import styled, { DefaultTheme, css } from 'styled-components';
import { darken } from 'polished';

import {
    TButtonColors,
    TButtonStyle,
    TMappedButtonColors,
    TStyledButtonProperties,
} from './button.types';

function manageButtonColors(
    theme: DefaultTheme,
    color?: TButtonColors
): TMappedButtonColors {
    const style = buttonStyles.find(
        style => style.name === color
    ) as TButtonStyle;

    return {
        backgroundColor:
            style.backgroundColor === 'transparent'
                ? 'transparent'
                : theme.color[style.backgroundColor],
        borderColor:
            style.borderColor === 'transparent'
                ? 'transparent'
                : theme.color[style.borderColor],
        textColor: theme.color[style.color],
    };
}

function isTransparent(color: TButtonColors): boolean {
    return color.includes('transparent');
}

export const StyledLink = styled.a<TStyledButtonProperties>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ fullWidth, fullWidthOnMobileOnly }) =>
        fullWidth || fullWidthOnMobileOnly ? '100%' : 'fit-content'};
    padding: 1.3rem 1.5rem;
    overflow: hidden;
    color: ${({ theme, buttonColor, disabled }) =>
        disabled
            ? theme.color.grey5
            : manageButtonColors(theme, buttonColor).textColor};
    font-weight: ${properties => properties.theme.font.weight.bold};
    font-size: 1.4rem;
    font-family: ${properties => properties.theme.font.family.secondary};
    line-height: inherit;
    letter-spacing: 0.05rem;
    text-align: center;
    text-decoration: none;
    background-color: ${({ theme, buttonColor, disabled }) =>
        disabled
            ? theme.color.grey2
            : manageButtonColors(theme, buttonColor).backgroundColor};
    border: 0.1rem solid
        ${({ theme, buttonColor }) =>
            manageButtonColors(theme, buttonColor).borderColor};
    border-radius: ${({ theme }) => theme.borderRadius.default};
    cursor: ${properties => (properties.disabled ? 'auto' : 'pointer')};

    &:before,
    &:after {
        position: absolute;
        top: 0;
        bottom: 0;
        display: block;
        width: 80%;
        background: ${({ theme, disabled }) =>
            disabled ? theme.color.grey2 : 'currentColor'};
        opacity: 0.1;
        transition: ${properties =>
            properties.disabled ? 'none' : 'transform 0.25s ease-out'};
        content: '';
    }

    &:before {
        left: -30%;
        transform: translateX(-100%) skew(-30deg);
    }

    &:after {
        right: -30%;
        transform: translateX(100%) skew(-30deg);
    }

    &:hover {
        &:before,
        &:after {
            transform: skew(-30deg);
        }
    }

    &:focus,
    &:active {
        color: ${({ theme, buttonColor, disabled }) => {
            if (disabled) {
                return theme.color.grey5;
            } else if (isTransparent(buttonColor)) {
                return darken(
                    0.1,
                    manageButtonColors(theme, buttonColor).textColor
                );
            } else {
                return manageButtonColors(theme, buttonColor).textColor;
            }
        }};
        background-color: ${({ theme, buttonColor, disabled }) =>
            disabled
                ? theme.color.grey2
                : darken(
                      0.1,
                      manageButtonColors(theme, buttonColor).backgroundColor
                  )};
        border-color: ${({ theme, buttonColor }) =>
            darken(0.2, manageButtonColors(theme, buttonColor).borderColor)};
        outline: 0.3rem solid
            ${({ theme, buttonColor }) =>
                isTransparent(buttonColor)
                    ? manageButtonColors(theme, buttonColor).textColor
                    : manageButtonColors(theme, buttonColor).backgroundColor};
        outline-offset: 0.1rem;
    }
    ${({ fullWidthOnMobileOnly, theme }) =>
        fullWidthOnMobileOnly
            ? css`
                  @media (min-width: ${theme.deprecatedScreen
                          .mobileLandscapeMinimum}) {
                      width: fit-content;
                  }
              `
            : ''}
`;

export const StyledText = styled.span`
    color: ${properties => properties.theme.color.grey5};
`;

const buttonStyles: TButtonStyle[] = [
    {
        name: 'purple',
        backgroundColor: 'purple',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentPurple',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'purple',
    },
    {
        name: 'transparentBorderedPurple',
        backgroundColor: 'transparent',
        borderColor: 'purple',
        color: 'purple',
    },
    {
        name: 'black',
        backgroundColor: 'grey7',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentBlack',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'grey7',
    },
    {
        name: 'transparentBorderedBlack',
        backgroundColor: 'transparent',
        borderColor: 'grey7',
        color: 'grey7',
    },
    {
        name: 'pink',
        backgroundColor: 'pink',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentPink',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'pink',
    },
    {
        name: 'transparentBorderedPink',
        backgroundColor: 'transparent',
        borderColor: 'pink',
        color: 'pink',
    },
    {
        name: 'yellow',
        backgroundColor: 'yellow',
        borderColor: 'transparent',
        color: 'grey7',
    },
    {
        name: 'transparentYellow',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'yellow',
    },
    {
        name: 'transparentBorderedYellow',
        backgroundColor: 'transparent',
        borderColor: 'yellow',
        color: 'yellow',
    },
    {
        name: 'indigo',
        backgroundColor: 'indigo',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentIndigo',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'indigo',
    },
    {
        name: 'transparentBorderedIndigo',
        backgroundColor: 'transparent',
        borderColor: 'indigo',
        color: 'indigo',
    },
    {
        name: 'green',
        backgroundColor: 'green',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentGreen',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'green',
    },
    {
        name: 'transparentBorderedGreen',
        backgroundColor: 'transparent',
        borderColor: 'green',
        color: 'green',
    },
    {
        name: 'orange',
        backgroundColor: 'orange',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentOrange',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'orange',
    },
    {
        name: 'transparentBorderedOrange',
        backgroundColor: 'transparent',
        borderColor: 'orange',
        color: 'orange',
    },
    {
        name: 'darkPurple',
        backgroundColor: 'darkPurple',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentDarkPurple',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'darkPurple',
    },
    {
        name: 'transparentBorderedDarkPurple',
        backgroundColor: 'transparent',
        borderColor: 'darkPurple',
        color: 'darkPurple',
    },
    {
        name: 'teal',
        backgroundColor: 'teal',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentTeal',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'teal',
    },
    {
        name: 'transparentBorderedTeal',
        backgroundColor: 'transparent',
        borderColor: 'teal',
        color: 'teal',
    },
    {
        name: 'white',
        backgroundColor: 'white',
        borderColor: 'transparent',
        color: 'grey7',
    },
    {
        name: 'transparentWhite',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        color: 'white',
    },
    {
        name: 'transparentBorderedWhite',
        backgroundColor: 'transparent',
        borderColor: 'white',
        color: 'white',
    },
];
